import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Atoms
import Text from "new_components/atoms/Text";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";

// Organism: Card Wrapper
const CardWrapper = styled.div`
  background-color: ${colors.white};
  border: ${({ selected }) =>
    selected ? `2px solid ${colors.primary[500]}` : "1px solid #DFDFDF"};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
  max-width: 300px;
  transition: box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  }
`;

// Atoms (Typography overrides)
const Title = styled(Text)`
  font-size: 24px;
  font-weight: bold;
  color: #2a3039;
  margin-bottom: 5px;
  line-height: 1.2;
`;

const Description = styled(Text)`
  font-size: 12px;
  color: #666d79;
  margin-top: 20px;
`;

const SubDescription = styled(Text)`
  font-size: 12px;
  color: #666d79;
`;

// Molecules: Score Text & Icon Container
const ScoreWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const ScoreText = styled(Text)`
  font-size: 24px;
  font-weight: bold;
  margin-right: 8px;
`;

const IconContainer = styled.div`
  bottom: -5px;
`;

// Molecules: Score Bar
const Bar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #dfdfdf;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 10px;
`;

const BarFill = styled.div`
  width: ${({ value }) => value}%;
  height: 100%;
  background-color: ${({ color }) => color};
`;

const OptionCard = ({
  option,
  onClick,
  selected,
  description,
  subtext,
  showScoreBar,
  showChangeArrow,
  score,
  barScoreOverride,
}) => {
  return (
    <CardWrapper onClick={onClick} selected={selected}>
      <Title typographyStyle={typography.body.semibold}>{option.name}</Title>

      {subtext && (
        <SubDescription typographyStyle={typography.smallText.regular}>
          {subtext}
        </SubDescription>
      )}

      {score !== undefined && (
        <ScoreWrapper>
          {showChangeArrow && score > 0 && (
            <Icon
              name={ICON_SET.arrowUp}
              color={colors.secondary[500]}
              size={36}
            />
          )}
          {showChangeArrow && score < 0 && (
            <Icon
              name={ICON_SET.arrowDown}
              color={colors.auxiliary.red[500]}
              size={36}
            />
          )}

          <ScoreText typographyStyle={typography.body.semibold}>
            {score}%
          </ScoreText>
        </ScoreWrapper>
      )}

      {showScoreBar && score !== undefined && (
        <Bar>
          <BarFill
            value={barScoreOverride || score}
            color={colors.primary[500]}
          />
        </Bar>
      )}

      {description && (
        <Description typographyStyle={typography.smallText.regular}>
          {description}
        </Description>
      )}
    </CardWrapper>
  );
};

OptionCard.propTypes = {
  option: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.number,
  }).isRequired,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  description: PropTypes.string,
  subtext: PropTypes.string,
  showScoreBar: PropTypes.bool,
  showChangeArrow: PropTypes.bool,
};

OptionCard.defaultProps = {
  onClick: undefined,
  selected: false,
  description: "",
  subtext: "",
  showScoreBar: false,
  showChangeArrow: false,
};

export default OptionCard;
